import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { z } from "zod";

import { api } from "@/api";
import { apiGet } from "@/api/client";
import { nativeAuthCapabilities } from "@/session/nativeAuth";
import { nativeAppleAvailable } from "@/session/nativeAppleAuth";
import {
  authenticateWallet,
  type ProviderAuthResult,
} from "@/session/walletAuth";

const providersSchema = z.object({
  providers: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      kind: z.enum(["oauth", "wallet"]),
      available: z.boolean(),
      reasonCode: z.string().nullable(),
      loginUrl: z.string().optional().nullable(),
      nativeAppleAvailable: z.boolean().optional(),
    }),
  ),
  pendingLink: z
    .object({
      provider: z.string(),
      requiresSignIn: z.boolean(),
      expiresIn: z.number(),
    })
    .nullable()
    .optional(),
});
export type AuthProvider = z.infer<typeof providersSchema>["providers"][number];
export type NativeProviderAuth = (
  provider: AuthProvider,
  next: string,
) => Promise<ProviderAuthResult>;

/** Browser login is a same-origin allauth POST, never an arbitrary redirect. */
export function safeProviderLoginUrl(provider: AuthProvider): string | null {
  if (provider.kind !== "oauth" || !provider.loginUrl) return null;
  const allowedPaths: Record<string, string> = {
    google: "/auth/google/login/",
    apple: "/auth/apple/login/",
    discord: "/auth/discord/login/",
    facebook: "/auth/facebook/login/",
    keycloak: "/auth/oidc/keycloak/login/",
  };
  try {
    const url = new URL(provider.loginUrl, window.location.origin);
    return url.origin === window.location.origin &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash &&
      url.pathname === allowedPaths[provider.id]
      ? url.pathname
      : null;
  } catch {
    return null;
  }
}

export type AppSession = {
  playerId: number;
  displayName: string;
  currency: string;
};

export type AuthDialogIntent = "login" | "register";

type SessionContextValue = {
  session: AppSession | null;
  authMode: "dev" | "direct" | "oidc" | "unavailable";
  loginUrl: string | null;
  registrationEnabled: boolean;
  loading: boolean;
  providers: AuthProvider[];
  providersLoading: boolean;
  providersError: string;
  pendingProvider: string | null;
  refreshProviders: () => Promise<void>;
  providerPending: boolean;
  isNative: boolean;
  nativeProviderAuthAvailable: boolean;
  nativeAppleAuthAvailable: boolean;
  authCallbackError: string;
  clearAuthCallbackError: () => void;
  authenticateProvider: (
    provider: AuthProvider,
    next: string,
  ) => Promise<ProviderAuthResult>;
  refreshSession: () => Promise<AppSession | null>;
  login: (name: string) => Promise<AppSession>;
  directLogin: (credentials: {
    username: string;
    password: string;
    rememberMe: boolean;
    totp?: string;
  }) => Promise<AppSession>;
  register: (payload: Parameters<typeof api.directRegister>[0]) => Promise<{
    accountCreated: boolean;
    session: AppSession | null;
    message?: string;
  }>;
  logout: () => Promise<void>;
  loginOpen: boolean;
  loginIntent: AuthDialogIntent;
  requestLogin: () => void;
  requestRegistration: () => void;
  dismissLogin: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

// Only these locally authored messages reach the UI. Unknown callback/native
// payloads may contain secrets or arbitrary text, so never interpolate them.
export function providerAuthErrorMessage(
  code: unknown,
  isNative = false,
): string {
  switch (code) {
    case 4001:
    case "4001":
    case "user_cancelled":
    case "PROVIDER_CANCELLED":
    case "access_denied":
      return "Sign-in was cancelled. You can try again or use your account credentials.";
    case "PROVIDER_MFA_REQUIRED":
    case "PROVIDER_MFA_POLICY_UNVERIFIED":
      return isNative
        ? "Your account needs an additional security check. Complete secure login on the Tilt Racing website; this app cannot open it yet."
        : "Your account needs an additional security check. Use MonkeyTilt secure login below.";
    case "PROVIDER_CALLBACK_UNVERIFIED":
      return "This sign-in method is awaiting callback approval for Tilt Racing. Use your account credentials instead.";
    case "PROVIDER_NOT_LINKED":
    case "PROVIDER_UNLINKED":
      return "This provider is not linked to an account. Sign in to an existing account, or explicitly register a new one, then connect the provider.";
    case "PROVIDER_CONFLICT":
    case "PROVIDER_LOOKUP_AMBIGUOUS":
    case "SESSION_SUBJECT_MISMATCH":
      return "We could not safely match this provider to your account. Sign in with your existing credentials; no accounts were linked.";
    case "PROVIDER_PROOF_EXPIRED":
    case "NATIVE_AUTH_EXPIRED":
      return "This sign-in attempt has expired. Please start again.";
    case "PROVIDER_REAUTH_REQUIRED":
      return "Sign in to your account again before connecting this provider.";
    case "SESSION_BANNED":
      return "This account cannot sign in. Contact support for help.";
    case "native_auth_unavailable":
    case "apple_native_unavailable":
      return "This sign-in method is unavailable in this app build. Use your account credentials instead.";
    default:
      return "Provider sign-in could not be completed. Please retry or use your account credentials.";
  }
}

function consumeAuthIntent(isNative: boolean): {
  intent: AuthDialogIntent;
  error: string;
} | null {
  const url = new URL(window.location.href);
  if (url.pathname !== "/app" && !url.pathname.startsWith("/app/")) return null;
  const intent = url.searchParams.get("auth");
  const hasError = url.searchParams.has("authError");
  if (!hasError && intent !== "login" && intent !== "register") return null;
  const error = hasError
    ? providerAuthErrorMessage(url.searchParams.get("authError"), isNative)
    : "";
  url.searchParams.delete("auth");
  url.searchParams.delete("authError");
  url.searchParams.delete("providerLink");
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
  return {
    intent: !hasError && intent === "register" ? "register" : "login",
    error,
  };
}

export function SessionProvider({
  children,
  nativeProviderAuth,
}: {
  children: ReactNode;
  nativeProviderAuth?: NativeProviderAuth;
}) {
  const [session, setSession] = useState<AppSession | null>(null);
  const [authMode, setAuthMode] =
    useState<SessionContextValue["authMode"]>("unavailable");
  const [loginUrl, setLoginUrl] = useState<string | null>(null);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginIntent, setLoginIntent] = useState<AuthDialogIntent>("login");
  const [providers, setProviders] = useState<AuthProvider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersError, setProvidersError] = useState("");
  const [pendingProvider, setPendingProvider] = useState<string | null>(null);
  const [providerPending, setProviderPending] = useState(false);
  const [authCallbackError, setAuthCallbackError] = useState("");
  const [nativeCapabilities, setNativeCapabilities] = useState({
    oauth: false,
    apple: false,
  });
  const providerFlight = useRef(false);
  const manifestFlight = useRef(false);
  const authRequestRef = useRef<
    ReturnType<typeof consumeAuthIntent> | undefined
  >(undefined);
  const isNative = "__TAURI_INTERNALS__" in window;
  const nativeProviderAuthAvailable = Boolean(
    isNative && nativeProviderAuth && nativeCapabilities.oauth,
  );
  const nativeAppleAuthAvailable = Boolean(
    isNative && nativeProviderAuth && nativeCapabilities.apple,
  );
  const clearAuthCallbackError = useCallback(
    () => setAuthCallbackError(""),
    [],
  );

  useEffect(() => {
    if (!isNative || !nativeProviderAuth) return;
    let active = true;
    void Promise.all([nativeAuthCapabilities(), nativeAppleAvailable()])
      .then(([capabilities, apple]) => {
        if (active) setNativeCapabilities({ oauth: capabilities.oauth, apple });
      })
      .catch(() => {
        if (active) setNativeCapabilities({ oauth: false, apple: false });
      });
    return () => {
      active = false;
    };
  }, [isNative, nativeProviderAuth]);

  const refreshProviders = useCallback(async () => {
    if (manifestFlight.current) return;
    manifestFlight.current = true;
    setProvidersLoading(true);
    setProvidersError("");
    setProviders([]);
    setPendingProvider(null);
    try {
      const data = await apiGet("/session/providers", providersSchema);
      setProviders(data.providers);
      if (data.pendingLink?.requiresSignIn && data.pendingLink.expiresIn > 0) {
        setPendingProvider(data.pendingLink.provider);
      }
    } catch {
      setProvidersError(
        "Provider sign-in could not be loaded. You can still use your account credentials or retry.",
      );
    } finally {
      manifestFlight.current = false;
      setProvidersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!loginOpen) return;
    const timer = window.setTimeout(() => void refreshProviders(), 0);
    return () => window.clearTimeout(timer);
  }, [loginOpen, refreshProviders]);

  const refreshSession = useCallback(async () => {
    const data = await api.session();
    setAuthMode(data.authMode);
    setLoginUrl(data.loginUrl);
    setRegistrationEnabled(data.registrationEnabled);
    const next =
      data.authenticated && data.player
        ? {
            playerId: data.player.playerId,
            displayName: data.player.displayName,
            currency: data.player.currency,
          }
        : null;
    setSession(next);
    if (next) setLoginOpen(false);
    return next;
  }, []);

  const authenticateProvider = useCallback(
    async (provider: AuthProvider, next: string) => {
      if (providerFlight.current)
        throw new Error("Another sign-in is already in progress.");
      const matches = providers.filter((item) => item.id === provider.id);
      const current =
        provider.id === "keycloak"
          ? { ...provider, available: Boolean(loginUrl), loginUrl }
          : matches.length === 1
            ? matches[0]
            : undefined;
      if (!current?.available)
        throw new Error(
          "This sign-in method is unavailable. Please refresh the options.",
        );
      providerFlight.current = true;
      setProviderPending(true);
      try {
        let result: ProviderAuthResult;
        if (
          current.kind === "wallet" &&
          (current.id === "metamask" || current.id === "phantom")
        ) {
          result = await authenticateWallet(current.id);
        } else if (
          isNative &&
          current.id !== "keycloak" &&
          nativeProviderAuth &&
          (nativeProviderAuthAvailable ||
            (current.id === "apple" &&
              current.nativeAppleAvailable &&
              nativeAppleAuthAvailable)) &&
          safeProviderLoginUrl(current)
        ) {
          result = await nativeProviderAuth(current, next);
        } else {
          throw new Error(
            "Secure sign-in is not available in this app build. Use your account credentials.",
          );
        }
        if (result.status !== "authenticated" && result.status !== "unlinked") {
          throw new Error("The sign-in response could not be verified.");
        }
        if (result.status === "authenticated" && !(await refreshSession())) {
          throw new Error(
            "Sign-in did not establish a session. Please try again.",
          );
        }
        return result;
      } finally {
        providerFlight.current = false;
        setProviderPending(false);
      }
    },
    [
      isNative,
      loginUrl,
      nativeProviderAuth,
      nativeProviderAuthAvailable,
      nativeAppleAuthAvailable,
      providers,
      refreshSession,
    ],
  );

  useEffect(() => {
    // Best-effort cleanup only: denied storage cannot block the cookie session.
    try {
      window.localStorage.removeItem("hw.session");
      window.localStorage.removeItem("hw.devPlayer");
    } catch {
      // Retired browser credentials are never read or used to authenticate.
    }
    // Preserve the consumed request across StrictMode's effect replay.
    if (authRequestRef.current === undefined)
      authRequestRef.current = consumeAuthIntent(isNative);
    const authRequest = authRequestRef.current;
    let active = true;
    void api
      .session()
      .then((data) => {
        if (!active) return;
        setAuthMode(data.authMode);
        setLoginUrl(data.loginUrl);
        setRegistrationEnabled(data.registrationEnabled);
        setSession(
          data.authenticated && data.player
            ? {
                playerId: data.player.playerId,
                displayName: data.player.displayName,
                currency: data.player.currency,
              }
            : null,
        );
        if (authRequest && (!data.authenticated || authRequest.error)) {
          setLoginIntent(
            authRequest.intent === "register" && data.registrationEnabled
              ? "register"
              : "login",
          );
          setAuthCallbackError(authRequest.error);
          setLoginOpen(true);
        }
      })
      .catch(() => {
        if (!active) return;
        setSession(null);
        if (authRequest) {
          setAuthCallbackError(authRequest.error);
          setLoginIntent("login");
          setLoginOpen(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isNative]);

  const login = useCallback(async (name: string) => {
    const data = await api.devLogin(name);
    const next: AppSession = {
      playerId: data.playerId,
      displayName: data.displayName,
      currency: data.currency,
    };
    setSession(next);
    setLoginOpen(false);
    return next;
  }, []);

  const directLogin = useCallback(
    async (credentials: {
      username: string;
      password: string;
      rememberMe: boolean;
      totp?: string;
    }) => {
      const data = credentials.totp
        ? await api.directMfa({
            ...credentials,
            totp: credentials.totp,
          })
        : await api.directLogin(credentials);
      if (!data.authenticated || !data.player) {
        throw new Error("Sign-in did not establish a session.");
      }
      const next: AppSession = {
        playerId: data.player.playerId,
        displayName: data.player.displayName,
        currency: data.player.currency,
      };
      setSession(next);
      setLoginOpen(false);
      return next;
    },
    [],
  );

  const register = useCallback(
    async (payload: Parameters<typeof api.directRegister>[0]) => {
      const data = await api.directRegister(payload);
      const next =
        data.authenticated && data.player
          ? {
              playerId: data.player.playerId,
              displayName: data.player.displayName,
              currency: data.player.currency,
            }
          : null;
      if (next) {
        setSession(next);
        setLoginOpen(false);
      }
      return {
        accountCreated: data.accountCreated,
        session: next,
        ...(data.message ? { message: data.message } : {}),
      };
    },
    [],
  );

  const logout = useCallback(async () => {
    await api.logout();
    setSession(null);
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      authMode,
      loginUrl,
      registrationEnabled,
      loading,
      providers,
      providersLoading,
      providersError,
      pendingProvider,
      refreshProviders,
      providerPending,
      isNative,
      nativeProviderAuthAvailable,
      nativeAppleAuthAvailable,
      authCallbackError,
      clearAuthCallbackError,
      authenticateProvider,
      refreshSession,
      login,
      directLogin,
      register,
      logout,
      loginOpen,
      loginIntent,
      requestLogin: () => {
        setAuthCallbackError("");
        setLoginIntent("login");
        setLoginOpen(true);
      },
      requestRegistration: () => {
        setAuthCallbackError("");
        setLoginIntent("register");
        setLoginOpen(true);
      },
      dismissLogin: () => {
        setAuthCallbackError("");
        setLoginOpen(false);
      },
    }),
    [
      session,
      authMode,
      loginUrl,
      registrationEnabled,
      loading,
      login,
      directLogin,
      register,
      logout,
      loginOpen,
      loginIntent,
      providers,
      providersLoading,
      providersError,
      pendingProvider,
      refreshProviders,
      providerPending,
      isNative,
      nativeProviderAuthAvailable,
      nativeAppleAuthAvailable,
      authCallbackError,
      clearAuthCallbackError,
      authenticateProvider,
      refreshSession,
    ],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside SessionProvider");
  return value;
}
